import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { EditorState, Modifier, DraftHandleValue, convertToRaw, ContentState } from 'draft-js';

import Editor from '@draft-js-plugins/editor';
import createEmojiPlugin from '@draft-js-plugins/emoji';
import Tippy from '@tippyjs/react';

import draftToHtml from 'draftjs-to-html';
import { stateFromHTML } from 'draft-js-import-html';

// import 'emoji-mart/css/emoji-mart.css';
import 'draft-js/dist/Draft.css';
import '@draft-js-plugins/emoji/lib/plugin.css';

import './styles.scss';
import { BiError, BiInfoCircle } from 'react-icons/bi';
import { BsCheck } from 'react-icons/bs';

import { contarCaracteres } from './contagem';

const emojiPlugin = createEmojiPlugin({
  useNativeArt: false,
});
const { EmojiSuggestions, EmojiSelect } = emojiPlugin;

interface TextEditorProps {
  initialValue?: string;
  minLength?: number;
  maxLength?: number;
  className?: string;
  onChange: (content: string, contentLength?: { textLength: number; fullLength: number }) => void;
  disableEmoji?: boolean;
  ariaLabel?: string;
  /** `light`: superfície clara, para as telas da nova identidade. Sem a prop, a escura de sempre. */
  appearance?: TextEditorAppearance;
  placeholder?: string;
}

type TextEditorAppearance = 'dark' | 'light';

const SURFACE_CLASSES: Record<TextEditorAppearance, string> = {
  dark: 'bg-maastricht-blue',
  light: 'border border-surface-200 bg-brand-white text-content',
};

function TextEditor({
  initialValue,
  minLength,
  maxLength,
  className,
  onChange,
  disableEmoji,
  ariaLabel,
  appearance = 'dark',
  placeholder,
}: TextEditorProps) {
  const [editorState, setEditorState] = useState(
    EditorState.createWithContent(initialValue ? stateFromHTML(initialValue) : ContentState.createFromText(initialValue || '')),
  );

  const [characters, setCharacters] = useState<{ textLength: number; fullLength: number }>({ textLength: 0, fullLength: 0 });

  const editor = useRef<Editor>(null);

  const textLength = (plainText: string, rawContent: any) => {
    const contagem = contarCaracteres(plainText, rawContent);
    setCharacters(contagem);
    return contagem;
  };

  // Contagem inicial do conteúdo já presente no campo (texto padrão aplicado, rascunho recuperado,
  // realização em edição). Sem isso o contador exibe 0 sobre um campo cheio. Só exibição: não
  // dispara `onChange` nem toca no estado do componente pai.
  useEffect(() => {
    if (!initialValue) {
      return;
    }

    const content = editorState.getCurrentContent();
    textLength(content.getPlainText(), convertToRaw(content));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onStateChange = useCallback(
    (state: EditorState) => {
      const content = state.getCurrentContent();
      const rawContent = convertToRaw(content);
      const html = draftToHtml(rawContent);

      setEditorState(state);
      onChange(html, textLength(content.getPlainText(), rawContent));
    },
    [onChange],
  );

  const handlePaste = (text: string): DraftHandleValue => {
    const contentState = Modifier.replaceText(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      text,
      editorState.getCurrentInlineStyle(),
    );
    onStateChange(EditorState.push(editorState, contentState, 'insert-characters'));
    return 'handled';
  };

  return (
    <div className={`flex flex-col h-full relative ${className || ''}`}>
      {/* O clique na moldura só leva o cursor ao campo, para quem acerta o recuo com o mouse; pelo
          teclado o foco já cai direto no campo editável. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        data-appearance={appearance}
        className={`${SURFACE_CLASSES[appearance]} flex-1 overflow-y-auto mb-2 rounded-md p-2`}
        onClick={() => editor.current?.focus()}
      >
        <Editor
          editorState={editorState}
          ariaLabel={ariaLabel || 'Caixa de texto. em branco . Campo editável. O texto deverá ter ao menos 100 caracteres'}
          onChange={onStateChange}
          placeholder={placeholder}
          tabIndex={0}
          plugins={[emojiPlugin]}
          handlePastedText={handlePaste}
          ref={editor}
        />
      </div>
      <div className={`flex ${disableEmoji ? 'justify-end' : 'justify-between'} items-center`}>
        {disableEmoji ? (
          ''
        ) : (
          <>
            <div className="text-black relative" aria-labelledby="emojiButton" tabIndex={-1}>
              <EmojiSuggestions />
              <EmojiSelect closeOnEmojiSelect />
            </div>
            <span id="emojiButton" className="opacity-0 h-0 w-0">
              Adicionar Emoji botão
            </span>
          </>
        )}
        <div>
          {!!minLength || !!maxLength ? (
            <Tippy
              allowHTML={true}
              className="bg-black"
              maxWidth="none"
              content={
                <div style={{ width: 440 }} className="font-light p-3 space-y-3">
                  {!!minLength && (
                    <p
                      className={`m-0 flex items-center space-x-1 font-thin ${
                        characters.textLength < minLength ? 'text-red-200' : 'text-green-200'
                      }`}
                    >
                      {characters.textLength < minLength ? <BiError size={18} /> : <BsCheck size={18} />}{' '}
                      <span className="whitespace-nowrap">Tamanho de texto mínimo - sem contabilizar emojis:</span>
                      <strong>{minLength}</strong>
                    </p>
                  )}
                  {!!maxLength && (
                    <p
                      className={`m-0 flex items-center space-x-1 font-thin ${
                        characters.fullLength > maxLength
                          ? 'text-red-200'
                          : !!minLength && characters.textLength >= minLength
                            ? 'text-green-200'
                            : ''
                      }`}
                    >
                      {characters.fullLength > maxLength ? (
                        <BiError size={18} />
                      ) : !!minLength && characters.textLength >= minLength ? (
                        <BsCheck size={18} />
                      ) : null}
                      <span className="whitespace-nowrap">Tamanho de texto máximo:</span>
                      <strong>{maxLength}</strong>
                    </p>
                  )}
                </div>
              }
            >
              <p className="m-0 flex items-center space-x-1">
                <span>Caracteres:</span>{' '}
                <span>
                  {characters.fullLength}
                  {maxLength && '/' + maxLength}
                </span>
                <BiInfoCircle aria-hidden="true" className="hidden lg:block relative -mt-1" />
              </p>
            </Tippy>
          ) : (
            <p className="m-0 block">
              <span>Caracteres:</span> {characters.fullLength}
              {maxLength && '/' + maxLength}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default TextEditor;
